/*Bearer ${token}`
 * Global themed confirmation dialog.
 *
 * Usage (anywhere in the app):
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, message, confirmLabel, variant });
 *   if (!ok) return;
 *
 * variant: "danger" (default red confirm) | "warning" | "info"
 */
import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { TC } from "../tokens/text";

// ── Theme tokens (light theme) ───────────────────────────────────────────────
const PAPER_SX = {
  backgroundImage: "none",
  background: "#ffffff",
  border: "1px solid #d8e6f7",
  borderRadius: "16px",
  boxShadow: "0 8px 40px rgba(0,87,216,0.12), 0 2px 8px rgba(0,0,0,0.06)",
  minWidth: 360,
  maxWidth: 480,
};

const VARIANT_MAP = {
  danger: {
    iconBg: "rgba(211,47,47,0.08)",
    iconBorder: "rgba(211,47,47,0.25)",
    Icon: DeleteOutlineIcon,
    iconColor: "#d32f2f",
    bodyBg: "rgba(211,47,47,0.05)",
    bodyBorder: "rgba(211,47,47,0.18)",
    confirmBg: "linear-gradient(135deg,#ef5350,#b71c1c)",
    confirmHover: "linear-gradient(135deg,#f44336,#c62828)",
    confirmShadow: "0 4px 18px rgba(239,83,80,0.35)",
  },
  warning: {
    iconBg: "rgba(245,124,0,0.08)",
    iconBorder: "rgba(245,124,0,0.25)",
    Icon: WarningAmberRoundedIcon,
    iconColor: "#f57c00",
    bodyBg: "rgba(245,124,0,0.05)",
    bodyBorder: "rgba(245,124,0,0.18)",
    confirmBg: "linear-gradient(135deg,#fb8c00,#e65100)",
    confirmHover: "linear-gradient(135deg,#ffa726,#f57c00)",
    confirmShadow: "0 4px 18px rgba(251,140,0,0.35)",
  },
  info: {
    iconBg: "rgba(0,87,216,0.08)",
    iconBorder: "rgba(0,87,216,0.25)",
    Icon: InfoOutlinedIcon,
    iconColor: "#0057d8",
    bodyBg: "rgba(0,87,216,0.05)",
    bodyBorder: "rgba(0,87,216,0.18)",
    confirmBg: "linear-gradient(135deg,#0057d8,#003a99)",
    confirmHover: "linear-gradient(135deg,#1a6fe8,#0050c8)",
    confirmShadow: "0 4px 18px rgba(0,87,216,0.35)",
  },
};

// ── Context ──────────────────────────────────────────────────────────────────
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false });
  const resolveRef = useRef(null);

  const confirm = useCallback(({
    title = "Are you sure?",
    message = "",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    variant = "danger",
  } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, title, message, confirmLabel, cancelLabel, variant });
    });
  }, []);

  const handleClose = (result) => {
    setState((s) => ({ ...s, open: false }));
    resolveRef.current?.(result);
    resolveRef.current = null;
  };

  const { open, title, message, confirmLabel, cancelLabel, variant = "danger" } = state;
  const v = VARIANT_MAP[variant] || VARIANT_MAP.danger;
  const { Icon } = v;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={!!open}
        onClose={() => handleClose(false)}
        sx={(theme) => ({ zIndex: theme.zIndex.modal + 300 })}
        PaperProps={{
          sx: (theme) => ({
            backgroundImage: "none",
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "16px",
            boxShadow: "0 8px 40px rgba(0,87,216,0.12), 0 2px 8px rgba(0,0,0,0.06)",
            minWidth: 360,
            maxWidth: 480,
            color: theme.palette.text.primary,
            zIndex: theme.zIndex.modal + 200,
          }),
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
          <VuiBox display="flex" alignItems="center" gap={1.5}>
            <VuiBox
              sx={{
                background: v.iconBg,
                border: `1px solid ${v.iconBorder}`,
                borderRadius: "10px",
                p: 0.8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon sx={{ color: v.iconColor, fontSize: "1.8rem" }} />
            </VuiBox>
            <VuiTypography
              variant="h5"
              fontWeight="bold"
              sx={{ letterSpacing: "0.01em", color: (t) => t.palette.text.primary }}
            >
              {title}
            </VuiTypography>
          </VuiBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 1, px: 3 }}>
          <VuiBox
            sx={{
              background: v.bodyBg,
              border: `1px solid ${v.bodyBorder}`,
              borderRadius: "12px",
              px: 2.5,
              py: 1.75,
            }}
          >
            <VuiTypography
              variant="body2"
              sx={{ lineHeight: 1.7, fontSize: "0.92rem", whiteSpace: "pre-line", color: (t) => t.palette.text.primary }}
            >
              {message}
            </VuiTypography>
          </VuiBox>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5, justifyContent: "flex-end" }}>
          <VuiButton
            variant="outlined"
            size="medium"
            onClick={() => handleClose(false)}
            sx={{
              borderColor: "rgba(74,96,128,0.4)",
              color: (t) => t.palette.text.secondary,
              fontWeight: 600,
              "&:hover": { borderColor: "#0a1628", background: "rgba(10,22,40,0.04)" },
            }}
          >
            {cancelLabel}
          </VuiButton>
          <VuiButton
            size="medium"
            onClick={() => handleClose(true)}
            sx={{
              background: v.confirmBg,
              color: (t) => t.palette.common.white,
              fontWeight: 700,
              "&:hover": { background: v.confirmHover },
              boxShadow: v.confirmShadow,
            }}
          >
            {confirmLabel}
          </VuiButton>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}


